import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import SectionTabs from '../../components/SectionTabs';
import { VENUES_TABS } from '../../lib/section-tabs';
import { api, geocode } from '../../lib/api';
import type { Venue } from '../../types';
import VenueEditModal from '../../components/VenueEditModal';
import VenueSourceBadge from '../../components/VenueSourceBadge';
import NeighborhoodSelect from '../../components/NeighborhoodSelect';
import { distanceMiles } from '../../lib/eventFilters';
import { formatCategory, formatCategoryList, getVenueCategories, slugifyCategory } from '../../lib/venue-categories';
import VenueCategoryMultiSelect from '../../components/VenueCategoryMultiSelect';
import VenueFiltersBar from '../../components/VenueFiltersBar';
import {
  DEFAULT_ADMIN_VENUE_FILTERS,
  VENUE_UNCATEGORIZED,
  type VenueActiveFilter,
  type VenueConfidenceFilter,
  type VenueFilterState,
  type VenueReviewStatusFilter,
  type VenueScanFreshness,
  type VenueSocialFilter,
  filterVenues,
  venueReviewBucket,
} from '../../lib/venueFilters';
import {
  type VenueGroupByKey,
  type VenueGroupNode,
  groupVenues,
} from '../../lib/venueGrouping';
import { useStoredOrigin } from '../../lib/useStoredOrigin';
import BulkJobModal from '../../components/BulkJobModal';
import { submitJob, type JobKind, type JobTarget } from '../../lib/jobs';
import { confirmDialog, promptDialog, toast } from '../../lib/dialogs';

// ---------- Types ----------
type SortKey =
  | 'name'
  | 'neighborhood'
  | 'category'
  | 'social'
  | 'active'
  | 'lastScan'
  | 'confidence'
  | 'reviews'
  | 'rating';
type SortDir = 'asc' | 'desc';
type ViewMode = 'table' | 'cards';
type Density = 'compact' | 'comfortable';
type EnrichmentStatus = 'auto' | 'needs_review' | 'manual' | 'skipped';

const UNCATEGORIZED = VENUE_UNCATEGORIZED;
const PAGE_SIZE_DEFAULT = 50;
const STORAGE_PREFIX = 'mhevents.admin.venues.';

interface ScanResult { ingested: number; issues: string[] }
interface WebsiteScanResult { ingested: number; pagesFetched: number; pagesBlocked: number; issues: string[] }
interface CleanupResult { dryRun: boolean; matched: number; venues: Array<{ rowKey: string; name: string; reason: string }> }
interface ReassignResult {
  dryRun: boolean;
  includeManual?: boolean;
  total: number;
  changed: number;
  updated: number;
  movedToUnidentified?: number;
  duplicatesRemoved?: number;
  skippedManualCount?: number;
  skippedNoCoordsCount?: number;
  venues: Array<{ rowKey: string; name: string; from: string; to: string }>;
  skippedManual?: Array<{ rowKey: string; name: string; partitionKey: string }>;
  skippedNoCoords?: Array<{ rowKey: string; name: string; partitionKey: string }>;
}

interface RecoverManualResult {
  dryRun: boolean;
  total: number;
  stampedCount: number;
  orphansRemovedCount: number;
  stamped: Array<{ rowKey: string; name: string; partitionKey: string; inferred: string; reason: 'duplicate-resolved' | 'partition-differs' | 'already-manual' }>;
  orphansRemoved: Array<{ rowKey: string; name: string; partitionKey: string }>;
}

interface DiscoverFromEventsResult {
  dryRun: boolean;
  monthsBack: number;
  llmUsed: boolean;
  eventsScanned: number;
  handlesConsidered: number;
  candidatesCreated: number;
  candidatesRejected: number;
  venuesCreated: number;
  venuesMatched: number;
  venues: Array<{ rowKey: string; name: string; handle: string; occurrences: number; confidence: number; reason: string; viaPlaces: boolean; created: boolean }>;
  created: Array<{ rowKey: string; name: string; handle: string; occurrences: number; kind: string; confidence: number; reason: string }>;
}

interface FilterPreset {
  id: string;
  label: string;
  description: string;
  apply: () => void;
}

// ---------- Helpers ----------
function socialRank(v: Venue): number {
  return (v.instagramHandle ? 2 : 0) + (v.facebookUrl ? 1 : 0);
}

function compare(a: Venue, b: Venue, key: SortKey): number {
  switch (key) {
    case 'name': return a.name.localeCompare(b.name);
    case 'neighborhood': return (a.partitionKey || '').localeCompare(b.partitionKey || '');
    case 'category': return (getVenueCategories(a)[0] || '').localeCompare(getVenueCategories(b)[0] || '');
    case 'social': return socialRank(a) - socialRank(b);
    case 'active': return Number(a.active !== false) - Number(b.active !== false);
    case 'lastScan': {
      const at = a.lastSocialScanAt ? new Date(a.lastSocialScanAt).getTime() : 0;
      const bt = b.lastSocialScanAt ? new Date(b.lastSocialScanAt).getTime() : 0;
      return at - bt;
    }
    case 'confidence': {
      const av = typeof a.venueConfidence === 'number' ? a.venueConfidence : -1;
      const bv = typeof b.venueConfidence === 'number' ? b.venueConfidence : -1;
      return av - bv;
    }
    case 'reviews': return (a.userRatingCount ?? 0) - (b.userRatingCount ?? 0);
    case 'rating': return (a.rating ?? 0) - (b.rating ?? 0);
  }
}

function relativeAgo(iso?: string): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

function scanFreshnessOf(v: Venue): 'never' | 'stale' | 'recent' {
  if (!v.lastSocialScanAt) return 'never';
  const days = (Date.now() - new Date(v.lastSocialScanAt).getTime()) / 86_400_000;
  if (days > 14) return 'stale';
  return 'recent';
}

function confidenceTone(score: number | undefined): 'success' | 'warning' | 'danger' | 'secondary' {
  if (typeof score !== 'number') return 'secondary';
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'danger';
}

function parseReasons(json: string | undefined): string[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function businessStatusLabel(s: string | undefined): string | null {
  if (!s || s === 'OPERATIONAL') return null;
  if (s === 'CLOSED_PERMANENTLY') return 'Closed permanently';
  if (s === 'CLOSED_TEMPORARILY') return 'Closed temporarily';
  return s;
}

function formatMiles(d: number): string {
  if (!Number.isFinite(d)) return '—';
  if (d < 0.1) return '<0.1 mi';
  if (d < 10) return `${d.toFixed(1)} mi`;
  return `${Math.round(d)} mi`;
}

function csvCell(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportCsv(filename: string, rows: Venue[]): void {
  const headers = [
    'name', 'neighborhood', 'category', 'address', 'website',
    'instagramHandle', 'facebookUrl', 'phone',
    'active', 'businessStatus', 'venueConfidence',
    'userRatingCount', 'rating', 'lastSocialScanAt', 'lastWebsiteScanAt',
    'lat', 'lng', 'rowKey',
  ];
  const lines = [headers.join(',')];
  for (const v of rows) {
    lines.push([
      v.name,
      v.partitionKey,
      getVenueCategories(v).join('|'),
      v.address ?? '',
      v.website ?? '',
      v.instagramHandle ?? '',
      v.facebookUrl ?? '',
      // phone not in type, leave blank for forward-compat
      '',
      v.active === false ? 'inactive' : 'active',
      v.businessStatus ?? '',
      v.venueConfidence ?? '',
      v.userRatingCount ?? '',
      v.rating ?? '',
      v.lastSocialScanAt ?? '',
      v.lastWebsiteScanAt ?? '',
      v.lat ?? '',
      v.lng ?? '',
      v.rowKey,
    ].map(csvCell).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function loadStored<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveStored<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // ignore quota / privacy mode
  }
}

// ---------- Component ----------
export interface AdminVenuesPageProps {
  /**
   * Optional filter overrides applied on top of `DEFAULT_ADMIN_VENUE_FILTERS`
   * when the page first mounts. Used by the dedicated
   * `AdminPendingVenuesPage` wrapper at `/venues/pending` to land users in a
   * pre-filtered "Pending review" view without losing access to the rest of
   * the controls. Filters remain fully editable after mount.
   */
  initialFilterOverrides?: Partial<VenueFilterState>;
}

export default function AdminVenuesPage({ initialFilterOverrides }: AdminVenuesPageProps = {}) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState<string | null>(null);
  const [scanningSite, setScanningSite] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Venue | null>(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupReport, setCleanupReport] = useState<CleanupResult | null>(null);
  const [rescoring, setRescoring] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [reassignReport, setReassignReport] = useState<ReassignResult | null>(null);
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverReport, setRecoverReport] = useState<RecoverManualResult | null>(null);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [discoverReport, setDiscoverReport] = useState<DiscoverFromEventsResult | null>(null);
  const [opsOpen, setOpsOpen] = useState(false);

  // Filters — single shared `VenueFilterState` (mirrored to the shared
  // `VenueFiltersBar`). KPIs and presets call `setFilters` to mutate this.
  const [filters, setFilters] = useState<VenueFilterState>(() => ({
    ...DEFAULT_ADMIN_VENUE_FILTERS,
    ...(initialFilterOverrides ?? {}),
  }));
  const [showFilters, setShowFilters] = useState<boolean>(() => loadStored('showFilters', true));

  // Sort + group
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [groupBy, setGroupBy] = useState<VenueGroupByKey[]>(() => {
    const v2 = loadStored<VenueGroupByKey[] | null>('groupBy.v2', null);
    if (v2 && Array.isArray(v2)) return v2;
    const legacy = loadStored<string | null>('groupBy', null);
    if (legacy && legacy !== 'none') return [legacy as VenueGroupByKey];
    return [];
  });
  const [savingField, setSavingField] = useState<{ id: string; field: 'category' | 'active' | 'neighborhood' | 'review' } | null>(null);

  // View / density / pagination
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadStored<ViewMode>('viewMode', 'table'));
  const [density, setDensity] = useState<Density>(() => loadStored<Density>('density', 'comfortable'));
  const [pageSize, setPageSize] = useState<number>(() => loadStored('pageSize', PAGE_SIZE_DEFAULT));
  const [page, setPage] = useState(1);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Bulk progress
  /**
   * Currently-tracked server-side bulk job. The work is queued on the API
   * and survives tab close — this state only controls which job the modal
   * is polling. `null` = no modal open.
   */
  const [activeJob, setActiveJob] = useState<{ jobPk: string; jobId: string } | null>(null);

  const searchRef = useRef<HTMLInputElement | null>(null);

  // Home-base origin for the "miles from home" stat. Loaded from admin
  // settings (default "1257 Glen Laura Road") and geocoded once on mount
  // via the public /api/geocode endpoint (cached server-side, no API key in
  // browser).
  const [homeBase, setHomeBase] = useState<{ label: string; lat: number; lng: number } | null>(null);

  // ---------- Persistence ----------
  useEffect(() => { saveStored('viewMode', viewMode); }, [viewMode]);
  useEffect(() => { saveStored('density', density); }, [density]);
  useEffect(() => { saveStored('pageSize', pageSize); }, [pageSize]);
  useEffect(() => { saveStored('showFilters', showFilters); }, [showFilters]);
  useEffect(() => { saveStored('groupBy.v2', groupBy); }, [groupBy]);

  // Origin/distance live in the shared store; merge them in only when we
  // actually filter so we never need bridge effects (which previously caused
  // a render loop with the geocode fallback inside `useStoredOrigin`).
  const { origin: storedOrigin, maxDistanceMiles: storedMaxMiles, setOriginState } = useStoredOrigin();
  const effectiveFilters = useMemo<VenueFilterState>(
    () => ({ ...filters, origin: storedOrigin, maxDistanceMiles: storedMaxMiles }),
    [filters, storedOrigin, storedMaxMiles],
  );

  // ---------- Data load ----------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Venue[]>('/api/mgmt/venues');
      setVenues(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Load home-base address from admin settings, then geocode once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.get<{ homeBaseAddress?: string }>('/api/mgmt/settings');
        const addr = (s.homeBaseAddress || '1257 Glen Laura Road').trim();
        if (!addr) return;
        const r = await geocode(addr);
        if (cancelled || !r) return;
        setHomeBase({ label: addr, lat: r.lat, lng: r.lng });
      } catch {
        // Non-fatal — distance column just shows "—".
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ---------- Keyboard shortcuts ----------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isFormField =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);
      if (e.key === '/' && !isFormField) {
        e.preventDefault();
        const el = searchRef.current
          ?? (document.querySelector('input[type="search"]') as HTMLInputElement | null);
        el?.focus();
        el?.select();
      } else if (e.key === 'Escape' && isFormField && target === searchRef.current) {
        setFilters((prev) => ({ ...prev, search: '' }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ---------- Derived options ----------
  const neighborhoodOptions = useMemo(
    () => Array.from(new Set(venues.map((v) => v.partitionKey).filter(Boolean))).sort(),
    [venues],
  );

  // ---------- Filtering / sorting ----------
  const visibleVenues = useMemo(() => {
    // Apply shared filter state (search + neighborhoods/categories sets +
    // active/social/confidence/scan + origin/distance).
    const filtered = filterVenues(venues, effectiveFilters);
    const sorted = [...filtered].sort((a, b) => compare(a, b, sortKey));
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [venues, effectiveFilters, sortKey, sortDir]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [effectiveFilters, sortKey, sortDir, pageSize, viewMode, groupBy]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(visibleVenues.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedVenues = useMemo(
    () => visibleVenues.slice((safePage - 1) * pageSize, safePage * pageSize),
    [visibleVenues, safePage, pageSize],
  );

  // Grouping (multi-level). Flatten the tree to a list of leaf groups so the
  // existing table/cards renderer can show one section header per leaf.
  const groupedVenues = useMemo<Array<{ key: string; label: string; venues: Venue[] }>>(() => {
    if (groupBy.length === 0) {
      return [{ key: '__all__', label: '', venues: pagedVenues }];
    }
    const tree = groupVenues(pagedVenues, groupBy);
    const out: Array<{ key: string; label: string; venues: Venue[] }> = [];
    const walk = (nodes: VenueGroupNode[], path: string[], idPath: string) => {
      for (const n of nodes) {
        const nextLabels = [...path, n.label];
        const nextId = idPath ? `${idPath}/${n.key}:${n.value}` : `${n.key}:${n.value}`;
        if (n.children.length > 0) {
          walk(n.children, nextLabels, nextId);
        } else {
          out.push({ key: nextId, label: nextLabels.join(' › '), venues: n.venues });
        }
      }
    };
    walk(tree, [], '');
    return out;
  }, [pagedVenues, groupBy]);

  // ---------- KPI metrics ----------
  const metrics = useMemo(() => {
    let active = 0, inactive = 0, low = 0, closed = 0, noSocial = 0, stale = 0, never = 0, uncat = 0;
    let pendingReview = 0, declined = 0, dismissedLegacy = 0;
    for (const v of venues) {
      if (v.active === false) inactive++; else active++;
      if (typeof v.venueConfidence === 'number' && v.venueConfidence < 40) low++;
      if (v.businessStatus === 'CLOSED_PERMANENTLY' || v.businessStatus === 'CLOSED_TEMPORARILY') closed++;
      if (!v.instagramHandle && !v.facebookUrl) noSocial++;
      const f = scanFreshnessOf(v);
      if (f === 'stale') stale++;
      if (f === 'never') never++;
      if (getVenueCategories(v).length === 0) uncat++;
      // Review-status buckets disambiguate *why* a venue is inactive: pending
      // admin approval, explicitly declined, or legacy manually-dismissed
      // (predates the review gate, so `reviewStatus` is undefined).
      const bucket = venueReviewBucket(v);
      if (bucket === 'pending') pendingReview++;
      else if (bucket === 'declined') declined++;
      else if (bucket === 'dismissed-legacy') dismissedLegacy++;
    }
    return { total: venues.length, active, inactive, low, closed, noSocial, stale, never, uncat, pendingReview, declined, dismissedLegacy };
  }, [venues]);

  // ---------- Mutations ----------
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const clearFilters = () => {
    setFilters((prev) => ({
      ...DEFAULT_ADMIN_VENUE_FILTERS,
      origin: prev.origin,
      maxDistanceMiles: prev.maxDistanceMiles,
      sort: prev.sort,
    }));
  };

  const discover = async () => {
    setBusy(true); setError(null);
    try { await api.post('/api/mgmt/venues/discover'); await load(); }
    catch (e) { setError(`Discovery failed: ${String(e)}`); }
    finally { setBusy(false); }
  };

  const discoverFromEvents = async (dryRun: boolean) => {
    // Server-side bulk job (single-step). The actual scan runs on the
    // queue worker so it survives the 230s HTTP timeout, browser disconnects,
    // and Function App restarts. The modal polls progress; the final report
    // arrives in `resultJson` once the step completes.
    setDiscoverBusy(true); setError(null); setDiscoverReport(null);
    try {
      const opts = {
        dryRun,
        monthsBack: 12,
        minOccurrences: 1
      };
      const label = `Discover from events${dryRun ? ' (dry-run)' : ''} — ${opts.monthsBack}mo, min ${opts.minOccurrences}`;
      const res = await submitJob({
        kind: 'venue-discover-from-events',
        label,
        targets: [{ kind: 'venue-discover-from-events', label, opts }],
        paramsJson: JSON.stringify(opts)
      });
      setActiveJob({ jobPk: res.jobPk, jobId: res.jobId });
    } catch (e) {
      setError(`Discover from events failed: ${String(e)}`);
    } finally {
      setDiscoverBusy(false);
    }
  };

  const cleanupChains = async (dryRun: boolean) => {
    setCleanupBusy(true); setError(null); setCleanupReport(null);
    try {
      const r = await api.post<CleanupResult>(`/api/mgmt/venues/cleanup-chains${dryRun ? '?dryRun=1' : ''}`);
      setCleanupReport(r);
      if (!dryRun) await load();
    } catch (e) {
      setError(`Cleanup failed: ${String(e)}`);
    } finally { setCleanupBusy(false); }
  };

  const rescoreConfidence = async () => {
    setRescoring(true); setError(null);
    try {
      const r = await api.post<{ total: number; updated: number }>('/api/mgmt/venues/rescore');
      await load();
      setError(`Rescored ${r.updated} of ${r.total} venue${r.total === 1 ? '' : 's'}.`);
    } catch (e) {
      setError(`Rescore failed: ${String(e)}`);
    } finally { setRescoring(false); }
  };

  const reassignNeighborhoods = async (dryRun: boolean, includeManual = false) => {
    setReassigning(true); setError(null); setReassignReport(null);
    try {
      const params = new URLSearchParams();
      if (dryRun) params.set('dryRun', '1');
      if (includeManual) params.set('includeManual', '1');
      const qs = params.toString();
      const r = await api.post<ReassignResult>(`/api/mgmt/venues/reassign-neighborhoods${qs ? `?${qs}` : ''}`);
      setReassignReport(r);
      if (!dryRun) await load();
    } catch (e) {
      setError(`Reassign failed: ${String(e)}`);
    } finally { setReassigning(false); }
  };

  const recoverManualOverrides = async (dryRun: boolean) => {
    setRecoverBusy(true); setError(null); setRecoverReport(null);
    try {
      const r = await api.post<RecoverManualResult>(`/api/mgmt/venues/recover-manual-overrides${dryRun ? '?dryRun=1' : ''}`);
      setRecoverReport(r);
      if (!dryRun) await load();
    } catch (e) {
      setError(`Recover failed: ${String(e)}`);
    } finally { setRecoverBusy(false); }
  };

  const scan = async (v: Venue) => {
    setScanning(v.rowKey); setError(null);
    try {
      const r = await api.post<ScanResult>(`/api/mgmt/venues/${encodeURIComponent(v.rowKey)}/scan`);
      await load();
      if (r?.issues?.length) setError(`${v.name}: ${r.issues.join(' | ')}`);
    } catch (e) {
      setError(`Scan failed for ${v.name}: ${String(e)}`);
    } finally { setScanning(null); }
  };

  const scanSite = async (v: Venue) => {
    setScanningSite(v.rowKey); setError(null);
    try {
      const r = await api.post<WebsiteScanResult>(`/api/mgmt/venues/${encodeURIComponent(v.rowKey)}/scan-website`);
      await load();
      if (r?.issues?.length) setError(`${v.name} (site): ${r.issues.join(' | ')}`);
    } catch (e) {
      setError(`Site scan failed for ${v.name}: ${String(e)}`);
    } finally { setScanningSite(null); }
  };

  const updateCategories = async (v: Venue, nextCategories: string[]) => {
    // Normalize, dedupe, and ignore no-op writes (order-sensitive: a re-order
    // is a real change).
    const norm = Array.from(new Set(nextCategories.map(slugifyCategory).filter(Boolean)));
    const current = getVenueCategories(v);
    if (norm.length === current.length && norm.every((c, i) => c === current[i])) return;
    setSavingField({ id: v.rowKey, field: 'category' });
    setError(null);
    try {
      await api.patch(`/api/mgmt/venues/${encodeURIComponent(v.rowKey)}`, { categories: norm });
      setVenues((prev) =>
        prev.map((x) =>
          x.rowKey === v.rowKey
            ? { ...x, categories: norm, category: norm[0] }
            : x,
        ),
      );
    } catch (e) {
      setError(`Failed to update categories for ${v.name}: ${String(e)}`);
    } finally { setSavingField(null); }
  };

  const updateActive = async (v: Venue, next: 'active' | 'inactive' | 'dismissed') => {
    // Map the 3-state selector to the underlying (active, reviewStatus) pair.
    //   active    → active=true,  reviewStatus left as-is (clear 'dismissed'/'declined')
    //   inactive  → active=false, reviewStatus left as-is (legacy semantics: hides events)
    //   dismissed → active=false, reviewStatus='dismissed' (hides venue from list, KEEPS events)
    const current: 'active' | 'inactive' | 'dismissed' =
      v.reviewStatus === 'dismissed' ? 'dismissed' : v.active === false ? 'inactive' : 'active';
    if (current === next) return;
    setSavingField({ id: v.rowKey, field: 'active' });
    setError(null);
    try {
      const patch: Partial<Venue> & { enrichmentStatus?: EnrichmentStatus } =
        next === 'active'
          ? {
              active: true,
              // Re-activating a previously dismissed/declined venue: clear the
              // review state so the badge goes away and it lands back in the
              // approved bucket.
              ...(v.reviewStatus === 'dismissed' || v.reviewStatus === 'declined'
                ? { reviewStatus: 'approved' }
                : {}),
            }
          : next === 'dismissed'
            ? { active: false, reviewStatus: 'dismissed', enrichmentStatus: 'skipped' }
            : { active: false, enrichmentStatus: 'skipped' };
      const updated = await api.patch<Venue>(`/api/mgmt/venues/${encodeURIComponent(v.rowKey)}`, patch);
      setVenues((prev) => prev.map((x) => (x.rowKey === v.rowKey ? { ...x, ...updated } : x)));
    } catch (e) {
      setError(`Failed to update active state for ${v.name}: ${String(e)}`);
    } finally { setSavingField(null); }
  };

  /**
   * One-click promote / decline / dismiss for venues sitting in
   * `reviewStatus: 'pending'` (the bucket the venue identifier helper drops
   * new auto-created venues into). Approving sets `active: true` AND
   * `reviewStatus: 'approved'`; declining sets `active: false` AND
   * `reviewStatus: 'declined'` (hides venue + events); dismissing sets
   * `active: false` AND `reviewStatus: 'dismissed'` (hides the venue from
   * the active list but KEEPS its events visible — used when the venue
   * itself is noise but the events extracted from it are still useful).
   * The server side accepts these fields in a single PATCH.
   */
  const updateReview = async (v: Venue, decision: 'approved' | 'declined' | 'dismissed') => {
    setSavingField({ id: v.rowKey, field: 'review' });
    setError(null);
    try {
      const patch: Partial<Venue> = decision === 'approved'
        ? { reviewStatus: 'approved', active: true }
        : decision === 'dismissed'
          ? { reviewStatus: 'dismissed', active: false, enrichmentStatus: 'skipped' }
          : { reviewStatus: 'declined', active: false, enrichmentStatus: 'skipped' };
      const updated = await api.patch<Venue>(
        `/api/mgmt/venues/${encodeURIComponent(v.rowKey)}`,
        patch,
      );
      setVenues((prev) => prev.map((x) => (x.rowKey === v.rowKey ? { ...x, ...updated } : x)));
    } catch (e) {
      setError(`Failed to ${decision} ${v.name}: ${String(e)}`);
    } finally { setSavingField(null); }
  };

  const updateNeighborhood = async (v: Venue, nextNeighborhood: string) => {
    const trimmed = nextNeighborhood.trim();
    if (!trimmed || trimmed === v.partitionKey) return;
    setSavingField({ id: v.rowKey, field: 'neighborhood' });
    setError(null);
    try {
      // Backend stamps manualNeighborhoodOverride=true automatically when
      // partitionKey changes via PATCH, and consolidates any duplicate rows.
      const updated = await api.patch<Venue>(
        `/api/mgmt/venues/${encodeURIComponent(v.rowKey)}`,
        { partitionKey: trimmed }
      );
      setVenues((prev) => prev.map((x) => (x.rowKey === v.rowKey ? { ...x, ...updated } : x)));
    } catch (e) {
      setError(`Failed to update neighborhood for ${v.name}: ${String(e)}`);
    } finally { setSavingField(null); }
  };

  // ---------- Bulk operations ----------
  const selectedVenues = useMemo(
    () => venues.filter((v) => selected.has(v.rowKey)),
    [venues, selected],
  );

  const toggleSelectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOnPage = pagedVenues.every((v) => next.has(v.rowKey));
      if (allOnPage) pagedVenues.forEach((v) => next.delete(v.rowKey));
      else pagedVenues.forEach((v) => next.add(v.rowKey));
      return next;
    });
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  /**
   * Submit a server-side bulk job and open the progress modal. Replaces the
   * old client-side `for (const v of targets) await api.post(...)` loop —
   * which silently aborted whenever the user closed the tab. The new flow:
   *
   *   1. POST /api/mgmt/jobs (creates row + bulk-enqueues N step messages)
   *   2. Open modal that polls /api/mgmt/jobs/{pk}/{id} every 2.5s
   *   3. Closing the modal does NOT cancel — use the modal's Cancel button
   *
   * On terminal status the modal calls `load()` so the table reflects the
   * server-side changes.
   */
  const submitAndTrack = async (input: { kind: JobKind; label: string; targets: JobTarget[] }) => {
    if (!input.targets.length) return;
    try {
      const res = await submitJob(input);
      setActiveJob({ jobPk: res.jobPk, jobId: res.jobId });
    } catch (e) {
      toast.error(`Could not submit job: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const bulkSetActive = (next: boolean) => {
    if (!selectedVenues.length) return;
    return submitAndTrack({
      kind: 'venue-patch',
      label: `${next ? 'Activate' : 'Deactivate'} ${selectedVenues.length} venue${selectedVenues.length === 1 ? '' : 's'}`,
      targets: selectedVenues.map<JobTarget>((v) => ({
        kind: 'venue-patch',
        venueId: v.rowKey,
        label: v.name,
        patch: next ? { active: true } : { active: false, enrichmentStatus: 'skipped' }
      }))
    });
  };

  // ── Cooldown-aware bulk-scan helpers ─────────────────────────────────
  // Mirrors the Series page's three-mode pattern:
  //   stale = never-scanned OR cooldown elapsed (respects rate-limit windows)
  //   force = ignore cooldowns (most expensive)
  // Mode is applied across either the visible filtered view (`scope='visible'`)
  // or the current selection (`scope='selected'`).
  type ScanMode = 'stale' | 'force';
  type ScanKind = 'social' | 'site';
  type ScanScope = 'visible' | 'selected';

  function scanTargets(kind: ScanKind, mode: ScanMode, scope: ScanScope): Venue[] {
    const pool = scope === 'visible' ? visibleVenues : selectedVenues;
    const now = Date.now();
    return pool.filter((v) => {
      if (v.active === false) return false;
      if (kind === 'social') {
        if (!v.instagramHandle) return false;
        if (mode === 'force') return true;
        // stale: never scanned OR no cooldown gate set OR cooldown elapsed
        if (!v.lastSocialScanAt) return true;
        if (!v.nextScanEligibleAt) return true;
        return new Date(v.nextScanEligibleAt).getTime() <= now;
      }
      // site
      if (!v.website) return false;
      if (mode === 'force') return true;
      if (!v.lastWebsiteScanAt) return true;
      if (!v.nextWebsiteScanEligibleAt) return true;
      return new Date(v.nextWebsiteScanEligibleAt).getTime() <= now;
    });
  }

  const scanCounts = useMemo(() => ({
    visibleSocialStale: scanTargets('social', 'stale', 'visible').length,
    visibleSocialForce: scanTargets('social', 'force', 'visible').length,
    visibleSiteStale: scanTargets('site', 'stale', 'visible').length,
    visibleSiteForce: scanTargets('site', 'force', 'visible').length,
    selectedSocialStale: scanTargets('social', 'stale', 'selected').length,
    selectedSocialForce: scanTargets('social', 'force', 'selected').length,
    selectedSiteStale: scanTargets('site', 'stale', 'selected').length,
    selectedSiteForce: scanTargets('site', 'force', 'selected').length,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [visibleVenues, selectedVenues]);

  async function runScan(kind: ScanKind, mode: ScanMode, scope: ScanScope): Promise<void> {
    const targets = scanTargets(kind, mode, scope);
    const scopeLabel = scope === 'visible' ? 'in the current filtered view' : 'from the current selection';
    const kindLabel = kind === 'social' ? 'IG' : 'website';
    if (targets.length === 0) {
      toast.info(
        mode === 'stale'
          ? `No ${kindLabel} targets ${scopeLabel} are past their cooldown — try "force" if you want to rescan now.`
          : `No active venues with ${kindLabel === 'IG' ? 'Instagram handles' : 'websites'} ${scopeLabel}.`,
      );
      return;
    }
    const costNote = kind === 'social'
      ? ` Estimated Apify cost ~$${(targets.length * 0.005).toFixed(3)}.`
      : ' (no Apify cost)';
    const modeLabel = mode === 'force' ? 'FORCE rescan (ignore cooldowns)' : 'scan (respect cooldown)';
    if (!(await confirmDialog({
      title: `${kindLabel} scan`,
      body: `${modeLabel} ${kindLabel} for ${targets.length} venue${targets.length === 1 ? '' : 's'} ${scopeLabel}?${costNote}`,
      confirmLabel: 'Run scan',
      danger: mode === 'force',
    }))) return;
    const jobKind: JobKind = kind === 'social' ? 'venue-scan-ig' : 'venue-scan-website';
    await submitAndTrack({
      kind: jobKind,
      label: `Scan ${kindLabel} for ${targets.length} venue${targets.length === 1 ? '' : 's'} (${mode})`,
      targets: targets.map<JobTarget>((v) =>
        kind === 'social'
          ? { kind: 'venue-scan-ig', venueId: v.rowKey, label: v.name }
          : { kind: 'venue-scan-website', venueId: v.rowKey, label: v.name }
      )
    });
  }

  const bulkSetCategory = async () => {
    if (!selectedVenues.length) return;
    const choice = await promptDialog({
      title: 'Set categories',
      label: 'Category slugs (comma-separated)',
      body: 'Set categories for selected venues. e.g. "bar, music_venue". Leave blank to clear.',
    });
    if (choice == null) return;
    const slugs = Array.from(new Set(
      choice.split(',').map(slugifyCategory).filter(Boolean),
    ));
    // Mirror the per-venue patch contract: `categoriesJson` is the source of
    // truth, `category` is the legacy primary mirror. Empty array clears both.
    const patch = slugs.length
      ? { categoriesJson: JSON.stringify(slugs), category: slugs[0] }
      : { categoriesJson: '', category: '' };
    await submitAndTrack({
      kind: 'venue-patch',
      label: slugs.length ? `Set categories to "${slugs.join(', ')}"` : 'Clear categories',
      targets: selectedVenues.map<JobTarget>((v) => ({
        kind: 'venue-patch',
        venueId: v.rowKey,
        label: v.name,
        patch
      }))
    });
  };

  // ---------- Filter presets ----------
  const presets = useMemo<FilterPreset[]>(() => [
    {
      id: 'pending-review',
      label: 'Pending review',
      description: 'New venues awaiting admin approval',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'all', reviewStatus: 'pending' })),
    },
    {
      id: 'manually-dismissed-legacy',
      label: 'Manually dismissed (legacy)',
      description: 'Inactive venues from before the review gate \u2014 admin manually deactivated them',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'inactive', reviewStatus: 'dismissed-legacy' })),
    },
    {
      id: 'dismissed',
      label: 'Dismissed (events kept)',
      description: 'Venues admin removed from the active list while keeping their events visible',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'all', reviewStatus: 'dismissed' })),
    },
    {
      id: 'declined',
      label: 'Declined',
      description: 'Venues admin explicitly declined during review',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'all', reviewStatus: 'declined' })),
    },
    {
      id: 'needs-review',
      label: 'Needs review',
      description: 'Active venues with low confidence',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'active', confidence: 'low' })),
    },
    {
      id: 'no-category',
      label: 'No category',
      description: 'Active venues missing a category',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'active', categories: new Set([UNCATEGORIZED]) })),
    },
    {
      id: 'stale-scans',
      label: 'Stale scans',
      description: 'Venues with IG scanned > 14 days ago',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'active', social: 'ig', scanFreshness: 'stale' })),
    },
    {
      id: 'never-scanned',
      label: 'Never scanned',
      description: 'Active venues with IG that have never been scanned',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'active', social: 'ig', scanFreshness: 'never' })),
    },
    {
      id: 'closed',
      label: 'Closed',
      description: 'Venues marked closed by Google',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'all', confidence: 'closed' })),
    },
    {
      id: 'no-social',
      label: 'No social',
      description: 'Active venues with no IG and no Facebook',
      apply: () => setFilters((p) => ({ ...DEFAULT_ADMIN_VENUE_FILTERS, origin: p.origin, maxDistanceMiles: p.maxDistanceMiles, sort: p.sort, active: 'active', social: 'none' })),
    },
  ], []);

  // ---------- Active filter chips ----------
  const filterChips = useMemo<Array<{ label: string; onClear: () => void }>>(() => {
    const chips: Array<{ label: string; onClear: () => void }> = [];
    if (filters.search) {
      chips.push({ label: `Search: "${filters.search}"`, onClear: () => setFilters((p) => ({ ...p, search: '' })) });
    }
    for (const n of filters.neighborhoods) {
      chips.push({
        label: `Neighborhood: ${n}`,
        onClear: () => setFilters((p) => {
          const next = new Set(p.neighborhoods); next.delete(n);
          return { ...p, neighborhoods: next };
        }),
      });
    }
    for (const c of filters.categories) {
      const lbl = c === UNCATEGORIZED ? 'Uncategorized' : formatCategory(c);
      chips.push({
        label: `Category: ${lbl}`,
        onClear: () => setFilters((p) => {
          const next = new Set(p.categories); next.delete(c);
          return { ...p, categories: next };
        }),
      });
    }
    if (filters.active !== 'active') {
      chips.push({ label: `Active: ${filters.active}`, onClear: () => setFilters((p) => ({ ...p, active: 'active' as VenueActiveFilter })) });
    }
    if (filters.social !== 'all') {
      chips.push({ label: `Social: ${filters.social}`, onClear: () => setFilters((p) => ({ ...p, social: 'all' as VenueSocialFilter })) });
    }
    if (filters.confidence !== 'all') {
      chips.push({ label: `Confidence: ${filters.confidence}`, onClear: () => setFilters((p) => ({ ...p, confidence: 'all' as VenueConfidenceFilter })) });
    }
    if (filters.scanFreshness !== 'all') {
      chips.push({ label: `Scan: ${filters.scanFreshness}`, onClear: () => setFilters((p) => ({ ...p, scanFreshness: 'all' as VenueScanFreshness })) });
    }
    if (filters.reviewStatus !== 'all') {
      const reviewLabels: Record<VenueReviewStatusFilter, string> = {
        all: 'All',
        pending: 'Pending review',
        approved: 'Approved',
        declined: 'Declined',
        dismissed: 'Dismissed (events kept)',
        'dismissed-legacy': 'Manually dismissed (legacy)',
        'auto-declined': 'Auto-declined',
      };
      chips.push({ label: `Review: ${reviewLabels[filters.reviewStatus]}`, onClear: () => setFilters((p) => ({ ...p, reviewStatus: 'all' as VenueReviewStatusFilter })) });
    }
    return chips;
  }, [filters]);

  // ---------- Render helpers ----------
  const SortHeader = ({ label, k, className }: { label: string; k: SortKey; className?: string }) => {
    const active = sortKey === k;
    const indicator = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const sortAttr = !active ? 'none' : sortDir === 'asc' ? 'ascending' : 'descending';
    return (
      <th scope="col" aria-sort={sortAttr} className={className}>
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none text-reset fw-semibold"
          onClick={() => toggleSort(k)}
        >
          {label}{indicator}
        </button>
      </th>
    );
  };

  const allOnPageSelected = pagedVenues.length > 0 && pagedVenues.every((v) => selected.has(v.rowKey));
  const someOnPageSelected = pagedVenues.some((v) => selected.has(v.rowKey));

  // ---------- Sub-renderers ----------
  function SocialLinks({ v }: { v: Venue }) {
    if (!v.instagramHandle && !v.facebookUrl) return <span className="text-muted small">—</span>;
    return (
      <span className="small mh-venue-social">
        {v.instagramHandle && (
          <a
            href={`https://instagram.com/${v.instagramHandle}`}
            target="_blank"
            rel="noreferrer noopener"
            title={`@${v.instagramHandle}`}
          >IG</a>
        )}
        {v.instagramHandle && v.facebookUrl && <span aria-hidden="true"> · </span>}
        {v.facebookUrl && (
          <a href={v.facebookUrl} target="_blank" rel="noreferrer noopener" title="Facebook page">FB</a>
        )}
      </span>
    );
  }

  function ConfidenceCell({ v }: { v: Venue }) {
    const reasons = parseReasons(v.confidenceReasonsJson);
    const closedLabel = businessStatusLabel(v.businessStatus);
    const tooltip = reasons.length
      ? reasons.join(' · ')
      : 'No deductions — looks like a real, operating venue.';
    const reviewBits: string[] = [];
    if (typeof v.userRatingCount === 'number') {
      reviewBits.push(`${v.userRatingCount} review${v.userRatingCount === 1 ? '' : 's'}`);
    }
    if (typeof v.rating === 'number' && (v.userRatingCount ?? 0) > 0) {
      reviewBits.push(`★ ${v.rating.toFixed(1)}`);
    }
    if (v.hasOpeningHours === false) reviewBits.push('no hours');
    return (
      <div title={tooltip} className="mh-venue-confidence text-center">
        <span className={`mh-venue-confidence__score badge bg-${confidenceTone(v.venueConfidence)}`}>
          {typeof v.venueConfidence === 'number' ? v.venueConfidence : '—'}
        </span>
        {closedLabel && <div className="text-danger small mt-1">{closedLabel}</div>}
        {reviewBits.length > 0 && <div className="text-muted small mt-1">{reviewBits.join(' · ')}</div>}
      </div>
    );
  }

  function ScanCell({ v }: { v: Venue }) {
    const f = scanFreshnessOf(v);
    return (
      <div className="small">
        <div className={f === 'never' ? 'text-muted' : f === 'stale' ? 'text-warning fw-semibold' : ''}>
          {relativeAgo(v.lastSocialScanAt)}
        </div>
        {v.lastSocialScanAt && (
          <div className="text-muted">
            {(v.lastSocialScanCount ?? 0)} ingested
            {v.lastSocialScanError && (
              <span className="text-danger" title={v.lastSocialScanError}> · err</span>
            )}
          </div>
        )}
        {v.lastWebsiteScanAt && (
          <div className="text-muted small" title={`Site: ${v.lastWebsiteScanCount ?? 0} ingested`}>
            site {relativeAgo(v.lastWebsiteScanAt)}
          </div>
        )}
      </div>
    );
  }

  function CategoryEditor({ v }: { v: Venue }) {
    return (
      <VenueCategoryMultiSelect
        value={getVenueCategories(v)}
        onChange={(next) => void updateCategories(v, next)}
        disabled={savingField?.id === v.rowKey && savingField.field === 'category'}
        ariaLabel={`Categories for ${v.name}`}
        size="sm"
      />
    );
  }

  function ActiveEditor({ v }: { v: Venue }) {
    // Show a small review-status badge next to the Active/Inactive control so
    // admins can see *why* a venue is inactive at a glance — pending review,
    // explicitly declined, dismissed (events kept), or manually dismissed
    // before the review gate existed.
    const bucket = venueReviewBucket(v);
    const badge = bucket === 'pending'
      ? { tone: 'warning', label: 'Pending review' }
      : bucket === 'auto-declined'
        ? { tone: 'danger', label: 'Auto-declined' }
        : bucket === 'declined'
          ? { tone: 'danger', label: 'Declined' }
          : bucket === 'dismissed'
            ? { tone: 'info', label: 'Dismissed · events kept' }
            : bucket === 'dismissed-legacy'
              ? { tone: 'secondary', label: 'Legacy dismiss' }
              : null;
    const selectValue: 'active' | 'inactive' | 'dismissed' =
      v.reviewStatus === 'dismissed' ? 'dismissed' : v.active === false ? 'inactive' : 'active';
    const selectClass = selectValue === 'active'
      ? ''
      : selectValue === 'dismissed'
        ? 'border-info text-info'
        : 'border-danger text-danger';
    return (
      <div className="d-flex flex-column gap-1">
        <select
          className={`form-select form-select-sm ${selectClass}`}
          value={selectValue}
          onChange={(e) => updateActive(v, e.target.value as 'active' | 'inactive' | 'dismissed')}
          disabled={savingField?.id === v.rowKey && savingField.field === 'active'}
          aria-label={`Active state for ${v.name}`}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="dismissed">Dismissed (keep events)</option>
        </select>
        {badge && (
          <span className={`badge bg-${badge.tone} align-self-start`} title={`Review status: ${badge.label}`}>
            {badge.label}
          </span>
        )}
        {bucket === 'pending' && (
          <div className="btn-group btn-group-sm" role="group" aria-label={`Review actions for ${v.name}`}>
            <button
              type="button"
              className="btn btn-success"
              disabled={savingField?.id === v.rowKey && savingField.field === 'review'}
              onClick={() => void updateReview(v, 'approved')}
              title="Approve this venue: sets active=true and reviewStatus=approved. Surfaces the venue and its events on the public site."
            >
              <i className="bi bi-check2 me-1" aria-hidden="true" />
              Approve
            </button>
            <button
              type="button"
              className="btn btn-outline-info"
              disabled={savingField?.id === v.rowKey && savingField.field === 'review'}
              onClick={() => void updateReview(v, 'dismissed')}
              title="Dismiss this venue: sets active=false and reviewStatus=dismissed. Removes the venue from the active venues list but KEEPS its events visible on the public site."
            >
              <i className="bi bi-eye-slash me-1" aria-hidden="true" />
              Dismiss
            </button>
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={savingField?.id === v.rowKey && savingField.field === 'review'}
              onClick={() => void updateReview(v, 'declined')}
              title="Decline this venue: sets active=false and reviewStatus=declined. Hides this venue's events from the public site."
            >
              <i className="bi bi-x me-1" aria-hidden="true" />
              Decline
            </button>
          </div>
        )}
      </div>
    );
  }

  function NeighborhoodEditor({ v }: { v: Venue }) {
    return (
      <div className="d-flex flex-column gap-1">
        <NeighborhoodSelect
          value={v.partitionKey ?? ''}
          options={neighborhoodOptions}
          disabled={savingField?.id === v.rowKey && savingField.field === 'neighborhood'}
          ariaLabel={`Neighborhood for ${v.name}`}
          onChange={(next) => void updateNeighborhood(v, next)}
        />
        {v.manualNeighborhoodOverride && (
          <span
            className="badge bg-info-subtle text-info-emphasis align-self-start"
            title="An admin pinned this neighborhood. Bulk Reassign neighborhoods will skip this venue unless 'incl. manual' is used."
          >Manual</span>
        )}
      </div>
    );
  }

  function RowActions({ v }: { v: Venue }) {
    return (
      <div className="btn-group btn-group-sm" role="group" aria-label={`Actions for ${v.name}`}>
        <button type="button" className="btn btn-outline-primary" onClick={() => setEditing(v)} aria-label={`Edit ${v.name}`}>Edit</button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => scan(v)}
          disabled={!v.instagramHandle || scanning === v.rowKey}
          aria-label={`Scan ${v.name} Instagram for new events`}
          title={v.instagramHandle ? 'Pull recent IG posts and run them through the extractor' : 'No Instagram handle'}
        >{scanning === v.rowKey ? 'Scanning…' : 'Scan IG'}</button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => scanSite(v)}
          disabled={!v.website || scanningSite === v.rowKey}
          aria-label={`Scan ${v.name} website for new events`}
          title={v.website ? 'Crawl the venue website (respects robots.txt)' : 'No website'}
        >{scanningSite === v.rowKey ? 'Scanning…' : 'Scan site'}</button>
      </div>
    );
  }

  function ExpandedDetails({ v }: { v: Venue }) {
    const reasons = parseReasons(v.confidenceReasonsJson);
    const mapsLink = v.lat && v.lng
      ? `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lng}`
      : v.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.address)}` : null;
    const milesFromHome = homeBase
      ? distanceMiles(homeBase.lat, homeBase.lng, v.lat, v.lng)
      : Infinity;
    const neighborhood = v.partitionKey && v.partitionKey !== 'Unidentified' ? v.partitionKey : null;
    return (
      <div className="mh-venue-details p-3">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <h6 className="mb-2 small text-uppercase text-muted">Location</h6>
            {v.address && <div className="mh-venue-details__line">{v.address}</div>}
            {neighborhood && (
              <div className="mh-venue-details__line">
                Neighborhood: <strong>{neighborhood}</strong>
              </div>
            )}
            {homeBase && Number.isFinite(milesFromHome) && (
              <div className="mh-venue-details__line">
                <strong>{formatMiles(milesFromHome)}</strong>{' '}
                <span className="text-muted">from {homeBase.label}</span>
              </div>
            )}
            {v.lat && v.lng && (
              <div className="mh-venue-details__line text-muted small">{v.lat.toFixed(5)}, {v.lng.toFixed(5)}</div>
            )}
            {mapsLink && (
              <a className="small" href={mapsLink} target="_blank" rel="noreferrer noopener">Open in Google Maps ↗</a>
            )}
          </div>
          <div className="col-12 col-md-6">
            <h6 className="mb-2 small text-uppercase text-muted">Identity</h6>
            <div className="mh-venue-details__line font-monospace small">{v.rowKey}</div>
            {v.googleTypes && v.googleTypes.length > 0 && (
              <div className="mt-2">
                {v.googleTypes.map((t) => (
                  <span key={t} className="badge bg-light text-dark border me-1 mb-1">{t}</span>
                ))}
              </div>
            )}
            <div className="mt-3">
              <VenueSourceBadge venue={v} variant="block" />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <h6 className="mb-2 small text-uppercase text-muted">Enrichment</h6>
            <div className="mh-venue-details__block">
              <div className="mh-venue-details__line">Status: <strong>{v.enrichmentStatus}</strong></div>
              {typeof v.enrichmentConfidence === 'number' && (
                <div className="mh-venue-details__line">Match confidence: <strong>{v.enrichmentConfidence}</strong></div>
              )}
              {v.lastEnrichedAt && <div className="mh-venue-details__line">Enriched: {relativeAgo(v.lastEnrichedAt)}</div>}
              {v.lastSignalsRefreshedAt && <div className="mh-venue-details__line">Signals: {relativeAgo(v.lastSignalsRefreshedAt)}</div>}
              {v.lastEventCapturedAt && <div className="mh-venue-details__line">Last event captured: {relativeAgo(v.lastEventCapturedAt)}</div>}
            </div>
          </div>
          <div className="col-12 col-md-6">
            <h6 className="mb-2 small text-uppercase text-muted">Confidence reasons</h6>
            {reasons.length === 0 ? (
              <div className="mh-venue-details__line text-muted">No deductions.</div>
            ) : (
              <ul className="mh-venue-details__block mb-0 ps-3">
                {reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
          </div>
          {v.lastSocialScanError && (
            <div className="col-12">
              <h6 className="mb-2 small text-uppercase text-muted">Last scan error</h6>
              <pre className="small text-danger mb-0">{v.lastSocialScanError}</pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  function VenueCard({ v }: { v: Venue }) {
    const closedLabel = businessStatusLabel(v.businessStatus);
    const isInactive = v.active === false;
    return (
      <div className={`card h-100 mh-venue-card ${isInactive ? 'mh-venue-card--inactive' : ''}`}>
        <div className="card-body d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`sel-card-${v.rowKey}`}
                checked={selected.has(v.rowKey)}
                onChange={() => toggleSelect(v.rowKey)}
                aria-label={`Select ${v.name}`}
              />
            </div>
            <span className={`badge bg-${confidenceTone(v.venueConfidence)}`}>
              {typeof v.venueConfidence === 'number' ? v.venueConfidence : '—'}
            </span>
          </div>
          <h6 className="mt-2 mb-1">
            {v.name}
            {v.website && <> &nbsp;<a className="small" href={v.website} target="_blank" rel="noreferrer noopener">site ↗</a></>}
          </h6>
          <div className="text-muted small">
            {v.partitionKey}{getVenueCategories(v).length ? ` · ${formatCategoryList(getVenueCategories(v))}` : ''}
          </div>
          <div className="mt-1">
            <VenueSourceBadge venue={v} />
          </div>
          {closedLabel && <div className="small text-danger mt-1">{closedLabel}</div>}
          {isInactive && <div className="small text-danger mt-1">Inactive</div>}
          <div className="mt-auto pt-2 d-flex justify-content-between align-items-end">
            <div className="small text-muted">
              <SocialLinks v={v} />
              <div>{relativeAgo(v.lastSocialScanAt)}</div>
            </div>
            <RowActions v={v} />
          </div>
        </div>
      </div>
    );
  }

  // ---------- Render ----------
  return (
    <SectionTabs ariaLabel="Venues sections" items={VENUES_TABS} fluid>
      <div className="mh-admin-venues">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h1 className="mb-1">Venues</h1>
            <div className="text-muted small">
              {loading ? 'Loading…' : `${metrics.total} venue${metrics.total === 1 ? '' : 's'} tracked`}
              {' · '}
              <kbd>/</kbd> to focus search
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2" role="group" aria-label="Venue actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => exportCsv(`venues-${new Date().toISOString().slice(0, 10)}.csv`, visibleVenues)}
              disabled={visibleVenues.length === 0}
            >Export CSV</button>
            <button className="btn btn-primary" onClick={discover} disabled={busy}>
              {busy ? 'Discovering…' : 'Refresh discovery'}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setOpsOpen((v) => !v)}
              aria-expanded={opsOpen}
              aria-controls="venue-ops-panel"
            >
              {opsOpen ? 'Hide operations ▴' : 'Show operations ▾'}
            </button>
          </div>
        </div>

        {/* Operations panel — grouped admin actions with inline descriptions */}
        {opsOpen && (
          <div id="venue-ops-panel" className="mh-ops-panel mb-3" role="region" aria-label="Venue operations">
            <OpsGroup
              title="Discovery"
              description="Find new venues from Google Places and from @-mentions in extracted events."
            >
              <OpsAction
                label={discoverBusy ? 'Scanning…' : 'Discover from events'}
                description="Scan event captions for @-mentions and auto-create both venues and series. Reuses the series classifier."
                onClick={() => discoverFromEvents(false)}
                disabled={discoverBusy}
                tone="primary"
              />
              <OpsAction
                label={discoverBusy ? 'Scanning…' : 'Preview from events'}
                description="Show what would be created from event @-mentions — no writes."
                onClick={() => discoverFromEvents(true)}
                disabled={discoverBusy}
                tone="secondary"
              />
            </OpsGroup>

            <OpsGroup
              title="Cleanup &amp; scoring"
              description="Maintain venue quality: remove fast-food chains and refresh confidence scores."
            >
              <OpsAction
                label={cleanupBusy ? 'Working…' : 'Preview chain cleanup'}
                description="Show which existing venues match the fast-food chain filter. No changes are made."
                onClick={() => cleanupChains(true)}
                disabled={cleanupBusy}
                tone="secondary"
              />
              <OpsAction
                label="Cleanup chains"
                description="Mark all matching fast-food chain venues as inactive."
                onClick={() => cleanupChains(false)}
                disabled={cleanupBusy}
                tone="warning"
              />
              <OpsAction
                label={rescoring ? 'Rescoring…' : 'Rescore confidence'}
                description="Recompute confidence scores from existing Google signals. Does not call the Places API."
                onClick={rescoreConfidence}
                disabled={rescoring}
                tone="secondary"
              />
            </OpsGroup>

            <OpsGroup
              title="Neighborhoods"
              description="Re-assign venues to the correct neighborhood based on their coordinates."
            >
              <OpsAction
                label={reassigning ? 'Working…' : 'Preview reassign'}
                description="Show which venues would move to a different neighborhood. No changes are made."
                onClick={() => reassignNeighborhoods(true)}
                disabled={reassigning}
                tone="secondary"
              />
              <OpsAction
                label="Reassign neighborhoods"
                description="Re-assign venues from coordinates. Skips venues an admin has manually pinned."
                onClick={() => reassignNeighborhoods(false)}
                disabled={reassigning}
                tone="info"
              />
              <OpsAction
                label="Reassign (incl. manual)"
                description="Reassign every venue from coordinates, including ones that were manually pinned by an admin. Asks for confirmation."
                onClick={() => {
                  void (async () => {
                    if (await confirmDialog({
                      title: 'Override admin pins',
                      body: 'Override admin-pinned neighborhoods too? This will undo any manual neighborhood choices made in the venue map popup or edit modal.',
                      confirmLabel: 'Override',
                      danger: true,
                    })) {
                      void reassignNeighborhoods(false, true);
                    }
                  })();
                }}
                disabled={reassigning}
                tone="warning"
              />
              <OpsAction
                label={recoverBusy ? 'Working…' : 'Preview recover manual'}
                description="Preview which venues will be detected as previously-manual edits. No changes are made."
                onClick={() => recoverManualOverrides(true)}
                disabled={recoverBusy}
                tone="secondary"
              />
              <OpsAction
                label="Recover manual overrides"
                description="One-time backfill: stamp venues whose current neighborhood differs from the inferred one as manual, and remove duplicate orphan rows."
                onClick={() => recoverManualOverrides(false)}
                disabled={recoverBusy}
                tone="success"
              />
            </OpsGroup>

            <OpsGroup
              title="Bulk scans — Instagram"
              description="Re-scan IG handles for venues currently visible in the table."
            >
              <OpsAction
                label={`Scan stale IG (${scanCounts.visibleSocialStale})`}
                description="Scan IG for visible venues past their cooldown (or never scanned). Respects rate-limit windows."
                onClick={() => void runScan('social', 'stale', 'visible')}
                disabled={!!activeJob || scanCounts.visibleSocialStale === 0}
                tone="warning"
              />
              <OpsAction
                label={`Force IG (${scanCounts.visibleSocialForce})`}
                description="Force-rescan every visible active venue with an IG handle, ignoring cooldowns. Most expensive."
                onClick={() => void runScan('social', 'force', 'visible')}
                disabled={!!activeJob || scanCounts.visibleSocialForce === 0}
                tone="danger"
              />
            </OpsGroup>

            <OpsGroup
              title="Bulk scans — Websites"
              description="Re-scan venue websites currently visible in the table. No Apify cost."
            >
              <OpsAction
                label={`Scan stale sites (${scanCounts.visibleSiteStale})`}
                description="Scan websites for visible venues past their cooldown (or never scanned)."
                onClick={() => void runScan('site', 'stale', 'visible')}
                disabled={!!activeJob || scanCounts.visibleSiteStale === 0}
                tone="warning"
              />
              <OpsAction
                label={`Force sites (${scanCounts.visibleSiteForce})`}
                description="Force-rescan websites for every visible active venue, ignoring cooldowns."
                onClick={() => void runScan('site', 'force', 'visible')}
                disabled={!!activeJob || scanCounts.visibleSiteForce === 0}
                tone="danger"
              />
            </OpsGroup>
          </div>
        )}

        {/* KPI cards */}
        <div className="row g-2 mb-3 mh-venue-stats">
          <KpiCard label="Total" value={metrics.total} tone="primary" onClick={clearFilters} />
          <KpiCard label="Active" value={metrics.active} tone="success" onClick={() => setFilters((p) => ({ ...p, active: 'active' }))} />
          <KpiCard label="Inactive" value={metrics.inactive} tone="secondary" onClick={() => setFilters((p) => ({ ...p, active: 'inactive' }))} />
          <KpiCard label="Pending review" value={metrics.pendingReview} tone="warning" onClick={() => setFilters((p) => ({ ...p, active: 'all', reviewStatus: 'pending' }))} />
          <KpiCard label="Manually dismissed" value={metrics.dismissedLegacy} tone="secondary" onClick={() => setFilters((p) => ({ ...p, active: 'inactive', reviewStatus: 'dismissed-legacy' }))} />
          <KpiCard label="Declined" value={metrics.declined} tone="danger" onClick={() => setFilters((p) => ({ ...p, active: 'all', reviewStatus: 'declined' }))} />
          <KpiCard label="Low confidence" value={metrics.low} tone="danger" onClick={() => setFilters((p) => ({ ...p, active: 'active', confidence: 'low' }))} />
          <KpiCard label="Closed" value={metrics.closed} tone="warning" onClick={() => setFilters((p) => ({ ...p, active: 'all', confidence: 'closed' }))} />
          <KpiCard label="No social" value={metrics.noSocial} tone="info" onClick={() => setFilters((p) => ({ ...p, active: 'active', social: 'none' }))} />
          <KpiCard label="Stale scans" value={metrics.stale} tone="warning" onClick={() => setFilters((p) => ({ ...p, active: 'active', social: 'ig', scanFreshness: 'stale' }))} />
          <KpiCard label="Uncategorized" value={metrics.uncat} tone="secondary" onClick={() => setFilters((p) => ({ ...p, active: 'active', categories: new Set([UNCATEGORIZED]) }))} />
        </div>

        {/* Sticky toolbar */}
        <div className="mh-venue-toolbar mb-3">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="input-group input-group-sm mh-venue-search">
              <span className="input-group-text" aria-hidden="true">🔍</span>
              <input
                ref={searchRef}
                type="search"
                className="form-control"
                placeholder="Search name, address, handle, place id…"
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                aria-label="Search venues"
              />
              {filters.search && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setFilters((p) => ({ ...p, search: '' }))}
                  aria-label="Clear search"
                >×</button>
              )}
            </div>

            <div className="btn-group btn-group-sm" role="group" aria-label="View mode">
              <button
                type="button"
                className={`btn btn-outline-secondary ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                aria-pressed={viewMode === 'table'}
              >Table</button>
              <button
                type="button"
                className={`btn btn-outline-secondary ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                aria-pressed={viewMode === 'cards'}
              >Cards</button>
            </div>

            <div className="btn-group btn-group-sm" role="group" aria-label="Density">
              <button
                type="button"
                className={`btn btn-outline-secondary ${density === 'compact' ? 'active' : ''}`}
                onClick={() => setDensity('compact')}
                aria-pressed={density === 'compact'}
                title="Compact rows"
              >Compact</button>
              <button
                type="button"
                className={`btn btn-outline-secondary ${density === 'comfortable' ? 'active' : ''}`}
                onClick={() => setDensity('comfortable')}
                aria-pressed={density === 'comfortable'}
                title="Comfortable rows"
              >Comfy</button>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowFilters((s) => !s)}
              aria-expanded={showFilters}
              aria-controls="mh-venue-filters"
            >{showFilters ? 'Hide filters' : 'Show filters'}{filterChips.length > 0 && <> · <span className="badge bg-primary">{filterChips.length}</span></>}</button>

            <div className="ms-auto small text-muted">
              Showing {pagedVenues.length === 0 ? 0 : (safePage - 1) * pageSize + 1}
              –{(safePage - 1) * pageSize + pagedVenues.length} of {visibleVenues.length}
            </div>
          </div>

          {/* Quick presets */}
          <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
            <span className="small text-muted">Quick views:</span>
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={p.apply}
                title={p.description}
              >{p.label}</button>
            ))}
          </div>

          {/* Active filter chips */}
          {filterChips.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
              <span className="small text-muted">Filters:</span>
              {filterChips.map((c, i) => (
                <span key={i} className="badge bg-light text-dark border d-inline-flex align-items-center gap-1">
                  {c.label}
                  <button
                    type="button"
                    className="btn-close btn-close-sm mh-filter-chip-close"
                    aria-label={`Remove filter ${c.label}`}
                    onClick={c.onClear}
                  />
                </span>
              ))}
              <button type="button" className="btn btn-sm btn-link p-0" onClick={clearFilters}>Clear all</button>
            </div>
          )}
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div id="mh-venue-filters" className="card mb-3">
            <div className="card-body py-3">
              <VenueFiltersBar
                venues={venues}
                filters={filters}
                setFilters={(updater) => setFilters(updater)}
                mode="admin"
                showGrouping
                groupBy={groupBy}
                setGroupBy={setGroupBy}
                showSort={false}
                hideSearch
                originOverride={storedOrigin}
                maxDistanceMilesOverride={storedMaxMiles}
                onOriginChange={setOriginState}
                idPrefix="mh-admin-venues"
                toolbarRightSlot={(
                  <div>
                    <label htmlFor="venue-pagesize" className="form-label small mb-0 me-1">Per page</label>
                    <select id="venue-pagesize" className="form-select form-select-sm d-inline-block w-auto"
                      value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                      {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="alert alert-primary d-flex flex-wrap align-items-center gap-2 py-2" role="status">
            <strong>{selected.size}</strong> selected
            <div className="btn-group btn-group-sm ms-2">
              <button type="button" className="btn btn-outline-success" onClick={() => bulkSetActive(true)} disabled={!!activeJob}>Activate</button>
              <button type="button" className="btn btn-outline-danger" onClick={() => bulkSetActive(false)} disabled={!!activeJob}>Deactivate</button>
              <button type="button" className="btn btn-outline-secondary" onClick={bulkSetCategory} disabled={!!activeJob}>Set category…</button>
              <button type="button" className="btn btn-outline-warning" onClick={() => void runScan('social', 'stale', 'selected')} disabled={!!activeJob || scanCounts.selectedSocialStale === 0}
                title="Scan IG for selected venues that are past their cooldown (or never scanned)."
              >Scan IG stale ({scanCounts.selectedSocialStale})</button>
              <button type="button" className="btn btn-outline-danger" onClick={() => void runScan('social', 'force', 'selected')} disabled={!!activeJob || scanCounts.selectedSocialForce === 0}
                title="Force-rescan IG for ALL selected venues with a handle, ignoring cooldowns."
              >Force IG ({scanCounts.selectedSocialForce})</button>
              <button type="button" className="btn btn-outline-warning" onClick={() => void runScan('site', 'stale', 'selected')} disabled={!!activeJob || scanCounts.selectedSiteStale === 0}
                title="Scan websites for selected venues that are past their cooldown (or never scanned)."
              >Scan sites stale ({scanCounts.selectedSiteStale})</button>
              <button type="button" className="btn btn-outline-danger" onClick={() => void runScan('site', 'force', 'selected')} disabled={!!activeJob || scanCounts.selectedSiteForce === 0}
                title="Force-rescan websites for ALL selected venues, ignoring cooldowns."
              >Force sites ({scanCounts.selectedSiteForce})</button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => exportCsv(`venues-selected-${new Date().toISOString().slice(0, 10)}.csv`, selectedVenues)}
              >Export selected</button>
            </div>
            <button type="button" className="btn btn-sm btn-link ms-auto" onClick={clearSelection}>Clear selection</button>
          </div>
        )}

        {/* Server-side bulk job progress modal. The job runs entirely on the
            API; closing the modal does NOT abort it. */}
        <BulkJobModal
          jobPk={activeJob?.jobPk}
          jobId={activeJob?.jobId}
          onClose={() => setActiveJob(null)}
          onComplete={(job) => {
            // For the single-step discover-from-events job the final report
            // is JSON-encoded in `resultJson`. Parse it so the existing
            // "Discover from events" report panel renders the result.
            if (job.kind === 'venue-discover-from-events' && job.resultJson) {
              try {
                setDiscoverReport(JSON.parse(job.resultJson) as DiscoverFromEventsResult);
              } catch {
                // Best-effort — fall through to refresh.
              }
            }
            void load();
          }}
        />

        {/* Reports */}
        {cleanupReport && (
          <div className={`alert ${cleanupReport.dryRun ? 'alert-info' : 'alert-success'}`} role="status">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>{cleanupReport.dryRun ? 'Preview:' : 'Done:'} {cleanupReport.matched} venue(s) match the chain filter.</strong>
                {cleanupReport.venues.length > 0 && (
                  <ul className="mb-0 mt-2 small">
                    {cleanupReport.venues.slice(0, 25).map((v) => (
                      <li key={v.rowKey}>{v.name} <span className="text-muted">({v.reason})</span></li>
                    ))}
                    {cleanupReport.venues.length > 25 && (
                      <li className="text-muted">…and {cleanupReport.venues.length - 25} more</li>
                    )}
                  </ul>
                )}
              </div>
              <button type="button" className="btn-close" aria-label="Dismiss cleanup report" onClick={() => setCleanupReport(null)} />
            </div>
          </div>
        )}

        {reassignReport && (
          <div className={`alert ${reassignReport.dryRun ? 'alert-info' : 'alert-success'}`} role="status">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>
                  {reassignReport.dryRun
                    ? `Preview: ${reassignReport.changed} of ${reassignReport.total} venue(s) would be moved to a different neighborhood.`
                    : `Done: ${reassignReport.changed} of ${reassignReport.total} venue(s) were moved to a different neighborhood.`}
                </strong>
                {(reassignReport.duplicatesRemoved ?? 0) > 0 && (
                  <div className="small mt-1">
                    {reassignReport.dryRun
                      ? `${reassignReport.duplicatesRemoved} duplicate row(s) would be cleaned up.`
                      : `${reassignReport.duplicatesRemoved} duplicate row(s) cleaned up.`}
                  </div>
                )}
                {(reassignReport.movedToUnidentified ?? 0) > 0 && (
                  <div className="small mt-1">
                    {reassignReport.dryRun
                      ? `${reassignReport.movedToUnidentified} venue(s) would be moved to “Unidentified” (no recognised neighborhood for their coordinates).`
                      : `${reassignReport.movedToUnidentified} venue(s) moved to “Unidentified”.`}
                  </div>
                )}
                {(reassignReport.skippedNoCoordsCount ?? 0) > 0 && (
                  <div className="small mt-1 text-muted">
                    Skipped {reassignReport.skippedNoCoordsCount} venue(s) with no coordinates — their neighborhood was left unchanged.
                  </div>
                )}
                {(reassignReport.skippedManualCount ?? 0) > 0 && (
                  <div className="small mt-1 text-muted">
                    Skipped {reassignReport.skippedManualCount} venue(s) with a manual neighborhood override.
                    {!reassignReport.includeManual && ' Use "Reassign (incl. manual)" to override them.'}
                  </div>
                )}
                {reassignReport.venues.length > 0 && (
                  <ul className="mb-0 mt-2 small">
                    {reassignReport.venues.slice(0, 25).map((v) => (
                      <li key={v.rowKey}>
                        {v.name}: <span className="text-muted">{v.from}</span> → <strong>{v.to}</strong>
                      </li>
                    ))}
                    {reassignReport.venues.length > 25 && (
                      <li className="text-muted">…and {reassignReport.venues.length - 25} more</li>
                    )}
                  </ul>
                )}
              </div>
              <button type="button" className="btn-close" aria-label="Dismiss reassign report" onClick={() => setReassignReport(null)} />
            </div>
          </div>
        )}

        {recoverReport && (
          <div className={`alert ${recoverReport.dryRun ? 'alert-info' : 'alert-success'}`} role="status">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>
                  {recoverReport.dryRun
                    ? `Preview: ${recoverReport.stampedCount} venue(s) would be marked as manual${recoverReport.orphansRemovedCount ? `, ${recoverReport.orphansRemovedCount} duplicate orphan row(s) would be removed` : ''}.`
                    : `Done: ${recoverReport.stampedCount} venue(s) marked as manual${recoverReport.orphansRemovedCount ? `, ${recoverReport.orphansRemovedCount} duplicate orphan row(s) removed` : ''}.`}
                </strong>
                {recoverReport.stamped.length > 0 && (
                  <details className="mt-2">
                    <summary className="small">Detected manual edits ({recoverReport.stamped.length})</summary>
                    <ul className="mb-0 mt-2 small">
                      {recoverReport.stamped.slice(0, 50).map((v) => (
                        <li key={`${v.rowKey}-${v.partitionKey}`}>
                          {v.name}: kept at <strong>{v.partitionKey}</strong>{' '}
                          <span className="text-muted">(auto would have inferred {v.inferred}; reason: {v.reason})</span>
                        </li>
                      ))}
                      {recoverReport.stamped.length > 50 && (
                        <li className="text-muted">…and {recoverReport.stamped.length - 50} more</li>
                      )}
                    </ul>
                  </details>
                )}
                {recoverReport.orphansRemoved.length > 0 && (
                  <details className="mt-2">
                    <summary className="small">Duplicate orphans removed ({recoverReport.orphansRemoved.length})</summary>
                    <ul className="mb-0 mt-2 small">
                      {recoverReport.orphansRemoved.slice(0, 50).map((v, i) => (
                        <li key={`${v.rowKey}-${v.partitionKey}-${i}`}>
                          {v.name} <span className="text-muted">(orphan row at {v.partitionKey})</span>
                        </li>
                      ))}
                      {recoverReport.orphansRemoved.length > 50 && (
                        <li className="text-muted">…and {recoverReport.orphansRemoved.length - 50} more</li>
                      )}
                    </ul>
                  </details>
                )}
              </div>
              <button type="button" className="btn-close" aria-label="Dismiss recover report" onClick={() => setRecoverReport(null)} />
            </div>
          </div>
        )}

        {discoverReport && (
          <div className={`alert ${discoverReport.dryRun ? 'alert-info' : 'alert-primary'}`} role="status">
            <div className="d-flex justify-content-between align-items-start">
              <div className="flex-grow-1">
                <strong>
                  {discoverReport.dryRun ? 'Preview:' : 'Done:'} scanned {discoverReport.eventsScanned} events from the past {discoverReport.monthsBack} months
                  {' '}({discoverReport.handlesConsidered} @-handles classified{discoverReport.llmUsed ? ' via LLM' : ''}).
                </strong>
                <ul className="small mb-2 mt-1">
                  <li><strong>{discoverReport.venuesCreated}</strong> venue{discoverReport.venuesCreated === 1 ? '' : 's'} auto-created (Places-upgraded or handle-only candidate)</li>
                  <li><strong>{discoverReport.venuesMatched}</strong> matched existing venues by handle/name</li>
                  <li><strong>{discoverReport.candidatesCreated}</strong> series candidate{discoverReport.candidatesCreated === 1 ? '' : 's'} created in the same pass</li>
                  <li><strong>{discoverReport.candidatesRejected}</strong> handles rejected as noise</li>
                </ul>
                <p className="text-muted small mb-2">
                  All newly-created handle-only candidates are queued for enrichment, which will look up website + Facebook page via Google Custom Search automatically.
                </p>
                {discoverReport.venues && discoverReport.venues.length > 0 && (
                  <details className="small" open>
                    <summary>Venues ({discoverReport.venues.length})</summary>
                    <ul className="mb-0 mt-1">
                      {discoverReport.venues.slice(0, 50).map((v) => (
                        <li key={v.handle}>
                          <a href={`https://instagram.com/${v.handle}`} target="_blank" rel="noopener noreferrer">@{v.handle}</a>{' '}
                          <strong>{v.name}</strong>{' '}
                          <span className="badge bg-light text-dark">{v.viaPlaces ? 'Places' : 'handle-only'}</span>{' '}
                          <span className="badge bg-light text-dark">{v.created ? 'created' : 'matched'}</span>{' '}
                          <span className="text-muted">({v.occurrences}×, conf {v.confidence}) — {v.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {discoverReport.created.length > 0 && (
                  <details className="small mt-1">
                    <summary>Series candidates ({discoverReport.created.length})</summary>
                    <ul className="mb-0 mt-1">
                      {discoverReport.created.slice(0, 50).map((c) => (
                        <li key={c.handle}>
                          <strong>{c.name}</strong> @{c.handle}{' '}
                          <span className="badge bg-light text-dark">{c.kind}</span>{' '}
                          <span className="text-muted">({c.occurrences}×, conf {c.confidence}) — {c.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <button type="button" className="btn-close" aria-label="Dismiss discover report" onClick={() => setDiscoverReport(null)} />
            </div>
          </div>
        )}

        {error && <div className="alert alert-warning" role="alert">{error}</div>}

        {/* Empty / loading states */}
        {!loading && visibleVenues.length === 0 && (
          <div className="text-center text-muted py-5">
            <p className="mb-2">No venues match the current filters.</p>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>Clear filters</button>
          </div>
        )}

        {/* Table view */}
        {viewMode === 'table' && visibleVenues.length > 0 && (
          <div className="table-responsive mh-venue-table-wrap">
            <table className={`table table-hover align-middle mh-venue-table ${density === 'compact' ? 'table-sm' : ''}`}>
              <thead>
                <tr>
                  <th scope="col" className="col-checkbox">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allOnPageSelected}
                      ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all on page"
                    />
                  </th>
                  <th scope="col" className="col-expand" aria-label="Expand row" />
                  <SortHeader label="Name" k="name" />
                  <SortHeader label="Neighborhood" k="neighborhood" />
                  <SortHeader label="Category" k="category" />
                  <SortHeader label="Social" k="social" />
                  <SortHeader label="Active" k="active" />
                  <SortHeader label="Confidence" k="confidence" />
                  <SortHeader label="Last scan" k="lastScan" />
                  <th scope="col" className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedVenues.map((group) => (
                  <Fragment key={group.key}>
                    {groupBy.length > 0 && (
                      <tr className="table-light">
                        <th colSpan={10} scope="rowgroup" className="small text-uppercase fw-semibold">
                          {group.label} <span className="text-muted fw-normal">({group.venues.length})</span>
                        </th>
                      </tr>
                    )}
                    {group.venues.map((v) => {
                      const isExpanded = expanded.has(v.rowKey);
                      const isSelected = selected.has(v.rowKey);
                      return (
                        <Fragment key={v.rowKey}>
                          <tr className={`${isSelected ? 'table-active' : ''} ${v.active === false ? 'mh-venue-row--inactive' : ''}`}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={isSelected}
                                onChange={() => toggleSelect(v.rowKey)}
                                aria-label={`Select ${v.name}`}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 mh-venue-expand-btn"
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? `Collapse ${v.name}` : `Expand ${v.name}`}
                                onClick={() => setExpanded((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(v.rowKey)) next.delete(v.rowKey); else next.add(v.rowKey);
                                  return next;
                                })}
                              >{isExpanded ? '▾' : '▸'}</button>
                            </td>
                            <td>
                              <strong>{v.name}</strong>
                              {v.website && <> &nbsp;<a className="small" href={v.website} target="_blank" rel="noreferrer noopener">site</a></>}
                              {v.address && (
                                <div className="text-muted small mh-venue-address" title={v.address}>{v.address}</div>
                              )}
                              {homeBase && (() => {
                                const m = distanceMiles(homeBase.lat, homeBase.lng, v.lat, v.lng);
                                if (!Number.isFinite(m)) return null;
                                return (
                                  <div className="small mh-venue-distance" title={`Distance from ${homeBase.label}`}>
                                    <i className="bi bi-geo-alt" aria-hidden="true" /> {formatMiles(m)}
                                  </div>
                                );
                              })()}
                            </td>
                            <td><NeighborhoodEditor v={v} /></td>
                            <td className="venue-cat-cell"><CategoryEditor v={v} /></td>
                            <td className="small"><SocialLinks v={v} /></td>
                            <td className="venue-status-cell"><ActiveEditor v={v} /></td>
                            <td className="small"><ConfidenceCell v={v} /></td>
                            <td className="small"><ScanCell v={v} /></td>
                            <td className="text-end"><RowActions v={v} /></td>
                          </tr>
                          {isExpanded && (
                            <tr className="mh-venue-details-row">
                              <td colSpan={10} className="bg-light"><ExpandedDetails v={v} /></td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Card view */}
        {viewMode === 'cards' && visibleVenues.length > 0 && (
          <div className="row g-2 mh-venue-cards">
            {groupedVenues.map((group) => (
              <Fragment key={group.key}>
                {groupBy.length > 0 && (
                  <div className="col-12">
                    <h6 className="mt-3 small text-uppercase text-muted">
                      {group.label} <span className="fw-normal">({group.venues.length})</span>
                    </h6>
                  </div>
                )}
                {group.venues.map((v) => (
                  <div key={v.rowKey} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                    <VenueCard v={v} />
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        )}

        {/* Pagination */}
        {visibleVenues.length > pageSize && (
          <nav aria-label="Venue pagination" className="mt-3 d-flex justify-content-between align-items-center">
            <span className="small text-muted">Page {safePage} of {totalPages}</span>
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-secondary" disabled={safePage <= 1} onClick={() => setPage(1)}>« First</button>
              <button type="button" className="btn btn-outline-secondary" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Prev</button>
              <button type="button" className="btn btn-outline-secondary" disabled={safePage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next ›</button>
              <button type="button" className="btn btn-outline-secondary" disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}>Last »</button>
            </div>
          </nav>
        )}

        <p className="small text-muted mt-3 mb-0">
          Click <strong>Scan IG</strong> on any venue with an Instagram handle to pull its 12 most recent
          posts and feed them through the extractor. Requires the Apify token to be set in Key Vault
          (<code>APIFY-TOKEN</code> — configurable from the admin Settings page).
        </p>

        <VenueEditModal
          venue={editing}
          onClose={() => setEditing(null)}
          neighborhoodOptions={neighborhoodOptions}
          onSaved={(updated) =>
            setVenues((prev) => prev.map((v) => (v.rowKey === updated.rowKey ? { ...v, ...updated } : v)))
          }
        />
      </div>
    </SectionTabs>
  );
}

// ---------- Tiny presentational components ----------
function OpsGroup({
  title, description, children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="mh-ops-group">
      <header className="mh-ops-group__head">
        <h2 className="mh-ops-group__title h6 mb-1">{title}</h2>
        <p className="mh-ops-group__desc text-muted small mb-0">{description}</p>
      </header>
      <div className="mh-ops-group__items">{children}</div>
    </section>
  );
}

function OpsAction({
  label, description, onClick, disabled, tone,
}: {
  label: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  tone: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
}) {
  return (
    <div className="mh-ops-action">
      <button
        type="button"
        className={`btn btn-outline-${tone} btn-sm mh-ops-action__btn`}
        onClick={onClick}
        disabled={disabled}
      >{label}</button>
      <p className="mh-ops-action__desc small text-muted mb-0">{description}</p>
    </div>
  );
}

function KpiCard({
  label, value, tone, onClick,
}: {
  label: string;
  value: number;
  tone: 'primary' | 'success' | 'secondary' | 'danger' | 'warning' | 'info';
  onClick?: () => void;
}) {
  const inner = (
    <>
      <div className="mh-kpi-label small text-uppercase text-muted">{label}</div>
      <div className={`mh-kpi-value text-${tone}`}>{value}</div>
    </>
  );
  return (
    <div className="col-6 col-sm-4 col-md-3 col-lg">
      {onClick ? (
        <button
          type="button"
          className="card mh-kpi-card w-100 h-100 text-start"
          onClick={onClick}
          aria-label={`Filter to ${label}`}
        >
          <div className="card-body py-2 px-3">{inner}</div>
        </button>
      ) : (
        <div className="card mh-kpi-card h-100">
          <div className="card-body py-2 px-3">{inner}</div>
        </div>
      )}
    </div>
  );
}


